import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { Link } from '../i18n/routing';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Menu, X, Sun, Moon, Smartphone, ChevronDown, ExternalLink } from 'lucide-react';
import TrainZillaHeaderLogo from './TrainZillaHeaderLogo';
import LocaleSelector from './LocaleSelector';
import { withUtm, trackedOutboundClick } from '../utils/outbound';
import { trackCtaClick } from '../services/analytics';
import { tr } from '../i18n/text';

const SIGN_IN_URL = withUtm('https://app.trainzilla.in/login', { campaign: 'header', content: 'sign_in' });

// Apple App Store Icon
const AppleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

// Google Play Icon
const PlayIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
  </svg>
);

interface NavigationItem {
  name: string;
  href: string;
  external?: boolean;
  subItems?: { name: string; href: string }[];
}

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const { t } = useLanguage();
  const { resolvedTheme, toggleTheme } = useTheme();
  const location = useLocation();
  const isDark = resolvedTheme === 'dark';

  const navigationItems: NavigationItem[] = [
    {
      name: t('common.navigation.solutions', { defaultValue: 'Solutions' }),
      href: '/solutions',
      subItems: [
        { name: t('common.navigation.platformOverview', { defaultValue: 'Platform Overview' }), href: '/solutions' },
        { name: tr("⚡ The Agent"), href: '/agent' },
        { name: tr("✨ AI Agent Integration"), href: '/solutions/ai-agent' },
        { name: tr("🤖 AI Fitness Coach"), href: '/ai-coach' },
        { name: t('common.navigation.clientManagement', { defaultValue: 'Client Management' }), href: '/solutions/client-management' },
        { name: t('common.navigation.workoutPlanning', { defaultValue: 'Workout Planning' }), href: '/solutions/workout-planning' },
        { name: t('common.navigation.businessAnalytics', { defaultValue: 'Business Analytics' }), href: '/solutions/business-analytics' },
      ],
    },
    {
      name: t('common.navigation.forCoaches', { defaultValue: 'For Coaches' }),
      href: '/benefits',
    },
    {
      name: t('common.navigation.forGyms', { defaultValue: 'For Gyms' }),
      href: '/gyms',
    },
    {
      name: t('common.navigation.marketplace', { defaultValue: 'Marketplace' }),
      href: '/marketplace',
    },
    {
      name: t('common.navigation.pricing', { defaultValue: 'Pricing' }),
      href: '/pricing',
    },
    {
      name: t('common.navigation.resources', { defaultValue: 'Resources' }),
      href: '/help-center',
      subItems: [
        { name: t('common.navigation.helpCenter', { defaultValue: 'Help Center' }), href: '/help-center' },
        { name: t('common.navigation.successStories', { defaultValue: 'Success Stories' }), href: '/success-stories' },
        { name: t('common.navigation.blog', { defaultValue: 'Blog' }), href: '/blog' },
        { name: t('common.navigation.webinars', { defaultValue: 'Webinars' }), href: '/webinars' },
        { name: t('common.navigation.compare', { defaultValue: 'Compare' }), href: '/compare' },
        { name: tr("What's new"), href: '/whats-new' },
      ]
    },
  ];


  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const isActiveRoute = (href: string) => location.pathname === href || location.pathname.startsWith(href + '/');

  // Inline styles for nav — avoids non-standard Tailwind opacity modifiers (/8, /92) breaking in dark mode
  const headerStyle: React.CSSProperties = (isScrolled || isMenuOpen)
    ? isDark
      ? {
          backgroundColor: 'rgba(2, 6, 23, 0.98)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        }
      : {
          backgroundColor: 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(148,163,184,0.3)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }
    : isDark
      ? {
          backgroundColor: 'transparent',
          borderBottom: '1px solid transparent',
        }
      : {
          backgroundColor: 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(241,245,249,0.8)',
        };

  const navLinkBase = `px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200`;
  const navLinkActive = isDark ? 'text-indigo-400' : 'text-indigo-600';
  const navLinkInactive = isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900';
  const navLinkActiveBg = isDark ? { backgroundColor: 'rgba(99,102,241,0.12)' } : { backgroundColor: '#eef2ff' };
  const navLinkHoverBg = isDark ? { backgroundColor: 'rgba(255,255,255,0.06)' } : {};

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300" style={headerStyle}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">

          {/* Logo */}
          <Link to="/" className="flex items-center group flex-shrink-0" aria-label={tr("TrainZilla Home")}>
            <TrainZillaHeaderLogo
              variant={isDark ? 'white' : 'primary'}
              className="h-8 lg:h-9 w-auto transition-transform duration-200 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <div key={item.name} className="relative group">
                {item.subItems ? (
                  <button
                    className={`${navLinkBase} flex items-center gap-1 ${isActiveRoute(item.href) ? navLinkActive : navLinkInactive}`}
                    style={isActiveRoute(item.href) ? navLinkActiveBg : {}}
                  >
                    {item.name}
                    <ChevronDown className="w-3.5 h-3.5 opacity-60 group-hover:rotate-180 transition-transform duration-200" />
                  </button>
                ) : item.external ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${navLinkBase} flex items-center gap-1 ${navLinkInactive}`}
                  >
                    {item.name}
                    <ExternalLink className="w-3 h-3 opacity-50" />
                  </a>
                ) : (
                  <Link
                    to={item.href}
                    className={`${navLinkBase} flex items-center ${isActiveRoute(item.href) ? navLinkActive : navLinkInactive}`}
                    style={isActiveRoute(item.href) ? navLinkActiveBg : {}}
                  >
                    {item.name}
                  </Link>
                )}

                {/* Dropdown */}
                {item.subItems && (
                  <div
                    className="absolute top-full left-0 mt-2 w-52 rounded-2xl shadow-2xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-1 group-hover:translate-y-0"
                    style={{
                      backgroundColor: isDark ? '#0f172a' : '#ffffff',
                      border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(226,232,240,1)',
                    }}
                  >
                    <div className="p-2">
                      {item.subItems.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          className={`block px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                            isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'
                          }`}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                          }}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden xl:flex items-center gap-2">
            {/* Locale */}
            <LocaleSelector variant="compact" />

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              aria-label={tr("Toggle theme")}
              className={`p-2 rounded-lg transition-all duration-200 ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
              }}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Coach App Download CTA */}
            <div className="relative group">
              <button
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 border"
                style={isDark
                  ? { backgroundColor: 'rgba(99,102,241,0.1)', borderColor: 'rgba(99,102,241,0.3)', color: '#a5b4fc' }
                  : { backgroundColor: '#eef2ff', borderColor: '#c7d2fe', color: '#4f46e5' }
                }
              >
                <Smartphone className="w-3.5 h-3.5" />
                {t('common.actions.coachApp', { defaultValue: 'Coach App' })}
                <ChevronDown className="w-3 h-3 opacity-60 group-hover:rotate-180 transition-transform duration-200" />
              </button>

              {/* App store dropdown */}
              <div
                className="absolute top-full right-0 mt-2 w-56 rounded-2xl shadow-2xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-1 group-hover:translate-y-0"
                style={{
                  backgroundColor: isDark ? '#0f172a' : '#ffffff',
                  border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(226,232,240,1)',
                }}
              >
                <div className="p-3">
                  <p className="text-xs font-medium mb-2 px-1" style={{ color: 'var(--text-muted)' }}>
                    {t('common.actions.downloadCoachApp', { defaultValue: 'Download TrainZilla Coach' })}
                  </p>
                  <a
                    href="https://apps.apple.com/us/app/trainzilla-coach/id6760616851"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150"
                    style={{ color: isDark ? '#e2e8f0' : '#374151' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9' }}>
                      <AppleIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs opacity-60">{t('common.actions.downloadOn', { defaultValue: 'Download on the' })}</div>
                      <div className="text-sm font-semibold">{t('common.actions.appStore', { defaultValue: 'App Store' })}</div>
                    </div>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=in.trainzilla.coach"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150"
                    style={{ color: isDark ? '#e2e8f0' : '#374151' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = isDark ? 'rgba(255,255,255,0.06)' : '#f8fafc'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9' }}>
                      <PlayIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs opacity-60">{t('common.actions.getItOn', { defaultValue: 'Get it on' })}</div>
                      <div className="text-sm font-semibold">{t('common.actions.googlePlay', { defaultValue: 'Google Play' })}</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* Sign In */}
            <a
              href={SIGN_IN_URL}
              onClick={trackedOutboundClick('header_sign_in')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
            >
              {t('common.actions.signIn', { defaultValue: 'Sign In' })}
            </a>

            {/* Start Free Trial */}
            <a
              href="/free-trial"
              onClick={() => trackCtaClick('header_start_free_trial')}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-all duration-200 shadow-lg hover:-translate-y-0.5"
              style={{ boxShadow: '0 4px 14px rgba(99,102,241,0.35)' }}
            >
              {t('common.actions.startFreeTrial', { defaultValue: 'Start Free Trial' })}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex xl:hidden items-center gap-2">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              aria-label={tr("Toggle theme")}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={tr("Toggle menu")}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'text-white' : 'text-slate-700'}`}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div
            className="xl:hidden border-t overflow-hidden"
            style={{ 
              borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(226,232,240,1)',
              backgroundColor: isDark ? '#020617' : '#ffffff'
            }}
          >
            <div className="py-4 space-y-1 max-h-screen overflow-y-auto">
              {navigationItems.map((item) => (
                <div key={item.name}>
                  {item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {item.name}
                      <ExternalLink className="w-3 h-3 opacity-50" />
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className={`block px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        isActiveRoute(item.href)
                          ? isDark ? 'text-indigo-400' : 'text-indigo-600'
                          : isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'
                      }`}
                      style={isActiveRoute(item.href) ? navLinkActiveBg : {}}
                    >
                      {item.name}
                    </Link>
                  )}
                  {item.subItems && (
                    <div className="ml-4 mt-1 space-y-0.5">
                      {item.subItems.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          className={`block px-4 py-2 rounded-lg text-xs transition-colors ${
                            isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'
                          }`}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <div className="pt-4 border-t space-y-3" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(226,232,240,1)' }}>
                {/* App Downloads */}
                <div className="px-4">
                  <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>{t('common.actions.downloadCoachApp', { defaultValue: 'Download Coach App' })}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <a href="https://apps.apple.com/us/app/trainzilla-coach/id6760616851" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-colors"
                      style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0', color: isDark ? '#cbd5e1' : '#374151' }}>
                      <AppleIcon className="w-4 h-4" /> {t('common.actions.appStore', { defaultValue: 'App Store' })}
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=in.trainzilla.coach" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-colors"
                      style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0', color: isDark ? '#cbd5e1' : '#374151' }}>
                      <PlayIcon className="w-4 h-4" /> {t('common.actions.googlePlay', { defaultValue: 'Play Store' })}
                    </a>
                  </div>
                </div>

                <div className="px-4">
                  <LocaleSelector variant="compact" />
                </div>

                <div className="px-4 space-y-2">
                  <a href={SIGN_IN_URL} onClick={trackedOutboundClick('header_mobile_sign_in')} className="block text-center px-4 py-2.5 rounded-xl text-sm font-medium border transition-colors"
                    style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0', color: isDark ? '#cbd5e1' : '#374151' }}>
                    {t('common.actions.signIn', { defaultValue: 'Sign In' })}
                  </a>
                  <a href="/free-trial" onClick={() => trackCtaClick('header_mobile_start_free_trial')} className="block text-center px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors">
                    {t('common.actions.startFreeTrial', { defaultValue: 'Start Free Trial' })}
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
